import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { removeEmpty } from '../utils';

export interface ApiRequest<B = unknown, T = unknown> {
  body?: B;
  id?: number | string;
  ids?: number[] | string[];
  params?: T;
  observe: 'body';
  headers?: HttpHeaders;
  responseType?: 'json';
  url?: string;
}

export interface ApiParams {
  offset?: number;
  page?: number;
  size?: number;
  sort?: string;
  'sort.dir'?: SortDir;
  'sort.on'?: string;
  name?: string;
  allJobs?: boolean;
  anonymization?: boolean;
  instantSearch?: boolean;
  searchPhrase?: string;
  search?: string;
  assignedUser?: string;
  entityType?: string;
  entityId?: number;
  assign?: string;
  content?: [];
}

export enum SortDir {
  DESC = 'desc',
  ASC = 'asc',
  NONE = '',
}

@Injectable()
export class ApiService {
  protected http = inject(HttpClient);
  serviceUrl = '';
  config = environment;
  baseUrl = (this.config && `${this.config.API_BASE_URL}/${this.serviceUrl}`) || '';
  headers = new HttpHeaders({ 'Content-Type': 'application/json' });

  convertOptions<Z>(options: Partial<ApiRequest<Z>>): ApiRequest<HttpParams> {
    const params = new HttpParams({ fromObject: removeEmpty(options.params) });
    return {
      ...options,
      params,
      headers: this.headers,
    } as ApiRequest<HttpParams>;
  }

  get<Z = ApiParams>({ id, url, ...options }: Partial<ApiRequest<Z>> = {}): Observable<Z> {
    return this.http
      .get<Z>(this.buildUrl({ id, url }), this.convertOptions<Z>(options) as object)
      .pipe(catchError(this.handleError));
  }

  post<B = unknown, K = unknown>({
    id,
    url,
    body,
    ...options
  }: Partial<ApiRequest<B>>): Observable<K> {
    return this.http
      .post<K>(this.buildUrl({ id, url }), JSON.stringify(body), {
        ...(this.convertOptions(options) as object),
      })
      .pipe(catchError(this.handleError));
  }

  put<B = unknown, K = unknown>({
    id,
    url,
    body,
    ...options
  }: Partial<ApiRequest<B>>): Observable<K> {
    return this.http
      .put<K>(
        this.buildUrl({ id, url }),
        JSON.stringify(body),
        this.convertOptions(options) as object,
      )
      .pipe(catchError(this.handleError));
  }

  patch<B = unknown, K = unknown>({
    id,
    url,
    body,
    ...options
  }: Partial<ApiRequest<B>>): Observable<K> {
    return this.http
      .patch<K>(
        this.buildUrl({ id, url }),
        JSON.stringify(body),
        this.convertOptions(options) as object,
      )
      .pipe(catchError(this.handleError));
  }

  delete<B = unknown, K = unknown>({ id, url, ...options }: Partial<ApiRequest<B>>): Observable<K> {
    return this.http
      .delete<K>(this.buildUrl({ id, url }), this.convertOptions(options) as object)
      .pipe(catchError(this.handleError));
  }

  deleteMultiple<K = unknown>({ id, ids, url }: Partial<ApiRequest<ApiParams>>): Observable<K> {
    let queryParams = new HttpParams();
    if (id != null) {
      queryParams = queryParams.append('id', id);
    }
    if (ids?.length) {
      queryParams = queryParams.appendAll({ ids });
    }
    const buildUrl = this.buildUrl({ url });
    return this.http
      .delete<K>(buildUrl + '?' + queryParams.toString())
      .pipe(catchError(this.handleError));
  }

  handleError(error: unknown): Observable<never> {
    return throwError(() => error);
  }

  private buildUrl({ id, url = '' }: Partial<ApiRequest<ApiParams>>): string {
    return `${this.baseUrl}${url}${id ?? ''}`;
  }

  set url(url: string) {
    if (url) {
      this.serviceUrl = url;
      this.baseUrl = `${this.baseUrl}${this.serviceUrl}`;
    }
  }

  get url(): string {
    return this.serviceUrl;
  }
}
